import { useRef, useState } from 'react';
import { CONFIG } from '../data/catalog.js';
import { fmt } from '../lib/money.js';
import { useI18n } from '../lib/i18n.jsx';
import { lookupOrder, submitSlip } from '../lib/api.js';

const STATUS_CLASS = {
  awaiting_payment: 'wait', pending: 'wait',
  paid: 'ok', shipped: 'ship',
  cancelled: 'off', failed: 'off',
};
const STATUS_KEYS = ['awaiting_payment', 'pending', 'paid', 'shipped', 'cancelled', 'failed'];

export default function Track({ nav }) {
  const { t, x, lang } = useI18n();
  const [orderNo, setOrderNo] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [slipFile, setSlipFile] = useState(null);
  const [slipBusy, setSlipBusy] = useState(false);
  const [slipResult, setSlipResult] = useState(null);
  const fileRef = useRef(null);

  const dt = (s) => s ? new Date(s).toLocaleString(lang === 'en' ? 'en-GB' : 'th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : null;
  const statusLabel = (st) => STATUS_KEYS.includes(st) ? t(`st.${st}`) : st;

  const search = async (e) => {
    e?.preventDefault();
    setError(''); setNotFound(false); setOrder(null); setSlipResult(null); setSlipFile(null);
    if (!orderNo.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const res = await lookupOrder(orderNo.trim(), email.trim());
      // API คืนข้อมูลออเดอร์แบบ flat (res.order_no, res.lines, ...) ไม่ได้ห่อใน res.order
      if (res?.found) setOrder(res.order || res);
      else setNotFound(true);
    } catch {
      setNotFound(true);
    } finally {
      setBusy(false);
    }
  };

  const handleSlip = async () => {
    if (!slipFile || !order) return;
    setSlipBusy(true);
    try {
      const res = await submitSlip(order.order_no, slipFile);
      setSlipResult(res);
      if (res.order_status === 'paid') setOrder((o) => ({ ...o, status: 'paid' }));
    } catch {
      setSlipResult({ verify_state: 'error', message: t('conf.slipFail') });
    } finally {
      setSlipBusy(false);
    }
  };

  const isAwaitTransfer = order && order.status === 'awaiting_payment' && order.pay_method === 'transfer';
  const bank = order?.bank;

  return (
    <section className="blk">
      <div className="wrap">
        <div className="track">
          <div className="shead" style={{ marginBottom: 24 }}>
            <div><div className="sub">Order Tracking · {CONFIG.domain}</div><h2>{t('track.h')}</h2></div>
          </div>

          {/* Search form */}
          <form className="track-form" onSubmit={search}>
            <div className="field">
              <label>{t('track.orderNo')}</label>
              <input
                value={orderNo} onChange={(e) => setOrderNo(e.target.value)}
                placeholder={t('track.orderNoPh')}
                autoComplete="off"
              />
            </div>
            <div className="field">
              <label>{t('track.email')}</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
              />
            </div>
            {error && <div className="errnote">{error}</div>}
            <button className="btn full" type="submit" disabled={busy} style={{ padding: 14 }}>
              {busy ? t('track.finding') : t('track.find')}
            </button>
          </form>

          {notFound && (
            <div className="track-empty">
              <p>{t('track.none')}</p>
              <p className="sub">{t('track.noneSub')}</p>
            </div>
          )}

          {/* Order result */}
          {order && (
            <div className="track-result">
              {/* Status header */}
              <div className="track-status">
                <div className={`track-badge ${STATUS_CLASS[order.status] || ''}`}>
                  {statusLabel(order.status)}
                </div>
                <div className="track-no">{order.order_no}</div>
                <div className="track-date">{t('track.orderedAt', dt(order.created_at))}</div>
              </div>

              {/* Progress steps */}
              <TrackProgress status={order.status} t={t} />

              {/* Tracking number */}
              {order.tracking_no && (
                <div className="track-tracking">
                  <div className="k">{t('track.trackingNo')}</div>
                  <div className="v">{order.tracking_no}</div>
                  {order.shipped_at && <div className="track-date">{t('track.shippedAt', dt(order.shipped_at))}</div>}
                </div>
              )}

              {/* Order lines */}
              <div className="track-lines">
                <h4>{t('track.lines')}</h4>
                {(order.lines || []).map((l, i) => (
                  <div className="track-line" key={i}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {l.img && <img src={l.img} alt="" width="44" height="44" loading="lazy"
                        style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 3, border: '1px solid #e5e0d8', flexShrink: 0 }} />}
                      <span>{l.name}{l.variant ? ' · ' + l.variant : ''} ×{l.qty}</span>
                    </span>
                    <b>{fmt(l.line_total)}</b>
                  </div>
                ))}
                <div className="track-line"><span>{order.ship_cost === 0 ? t('track.shipFree') : t('co.shipRow')}</span><b>{order.ship_cost > 0 ? fmt(order.ship_cost) : t('co.freeWord')}</b></div>
                <div className="track-line tot"><span>{t('track.grand')}</span><b>{fmt(order.grand_total)}</b></div>
              </div>

              {/* Transfer: bank + slip upload */}
              {isAwaitTransfer && bank && (
                <div className="track-pay">
                  <h4>{t('track.bankInfo')}</h4>
                  <div className="bankbox" style={{ textAlign: 'left' }}>
                    <div className="k">{t('conf.bank')}</div>{x(bank.name)}<br />
                    <div className="k">{t('conf.acct')}</div><span className="acct-num">{bank.acct}</span><br />
                    <div className="k">{t('conf.holder')}</div>{bank.holder}<br />
                    <div className="k">{t('conf.amount')}</div><b>{fmt(order.grand_total)}</b>
                  </div>

                  {!slipResult?.verify_state || slipResult.verify_state === 'error' ? (
                    <div style={{ marginTop: 14 }}>
                      <div
                        className={`upload${slipFile ? ' filled' : ''}`}
                        onClick={() => fileRef.current?.click()}
                      >
                        {slipFile ? t('conf.slipReady') : t('conf.slipTap')}
                        <div className="sub">{slipFile ? slipFile.name : t('conf.slipTypes')}</div>
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={(e) => { setSlipFile(e.target.files?.[0] || null); setSlipResult(null); }} />
                      {slipFile && (
                        <button className="btn full" style={{ marginTop: 12, padding: 14 }} onClick={handleSlip} disabled={slipBusy}>
                          {slipBusy ? t('conf.slipChecking') : t('conf.slipSend')}
                        </button>
                      )}
                      {slipResult?.verify_state === 'error' && (
                        <div className="slip-msg slip-err">{slipResult.message}</div>
                      )}
                      <div className="slipok" style={{ justifyContent: 'center', marginTop: 14 }}>{t('conf.slipAuto')}</div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 14 }}>
                      <div className={`slip-msg ${slipResult.verify_state === 'verified' ? 'slip-ok' : 'slip-warn'}`}>
                        {slipResult.message}
                      </div>
                      {slipResult.verify_state !== 'verified' && (
                        <button className="btn ghost sm" style={{ marginTop: 10 }}
                          onClick={() => { setSlipResult(null); setSlipFile(null); }}>
                          {t('conf.slipRetry')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Paid info */}
              {order.paid_at && (
                <div className="track-paid-note">
                  {t('track.paidAt', dt(order.paid_at))}
                  {order.pay_method === 'stripe' && ' · Stripe'}
                  {order.pay_method === 'transfer' && t('track.viaTransfer')}
                </div>
              )}

              {/* Contact */}
              <div className="track-contact">
                {t('track.contactQ')} <b>{CONFIG.email}</b>
              </div>
            </div>
          )}

          <div style={{ marginTop: 28, textAlign: 'center' }}>
            <button className="btn ghost sm" onClick={() => nav('/')}>{t('track.back')}</button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrackProgress({ status, t }) {
  const steps = [
    { key: 'awaiting_payment', label: t('step.await') },
    { key: 'paid', label: t('step.paid') },
    { key: 'shipped', label: t('step.shipped') },
  ];
  const cancelled = status === 'cancelled' || status === 'failed';
  const idx = cancelled ? -1
    : status === 'shipped' ? 2
    : status === 'paid' ? 1
    : 0;

  return (
    <div className="track-progress">
      {steps.map((s, i) => (
        <div key={s.key} className={`track-step${i <= idx ? ' done' : ''}${i === idx ? ' current' : ''}`}>
          <div className="dot">{i <= idx ? '✓' : i + 1}</div>
          <div className="lbl">{s.label}</div>
        </div>
      ))}
      {cancelled && (
        <div className="track-step off current">
          <div className="dot">✕</div>
          <div className="lbl">{status === 'cancelled' ? t('st.cancelled') : t('st.failed')}</div>
        </div>
      )}
    </div>
  );
}
